import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import "@/i18n";
import axios from "axios";
import { AuthProvider, useAuth } from "@/components/AuthContext";
import AuthPage from "@/components/AuthPage";
import LandingPage from "@/components/LandingPage";
import UserDemo from "@/components/UserDemo";
import IntermediaryDashboard from "@/components/IntermediaryDashboard";
import DriverDashboard from "@/components/DriverDashboard";
import AnalyticsDashboard from "@/components/AnalyticsDashboard";
import NetworkMap from "@/components/NetworkMap";
import BeaconDashboard from "@/components/BeaconDashboard";
import Navigation from "@/components/Navigation";
import PublicTracking from "@/components/PublicTracking";
import MyShipments from "@/components/MyShipments";
import AdminDashboard from "@/components/AdminDashboard";
import VehicleManager from "@/components/VehicleManager";
import RouteSearch from "@/components/RouteSearch";
import RolePortal from "@/components/RolePortal";
import FinancialDashboard from "@/components/FinancialDashboard";
import Gamification from "@/components/Gamification";
import BagReputation from "@/components/BagReputation";
import LegalPage from "@/components/LegalPage";
import SuperAdminIntegrations from "@/components/SuperAdminIntegrations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppContent() {
  const { user, loading: authLoading, logout } = useAuth();
  const [currentView, setCurrentView] = useState("landing");
  const [demoMode, setDemoMode] = useState(false);
  const [portalRole, setPortalRole] = useState(null); // selected role from RolePortal
  const [stats, setStats] = useState(null);
  const [bags, setBags] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [points, setPoints] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Public deep-link routes (no auth): /bag/:code, /termos, /privacidade
  const [publicRoute] = useState(() => {
    if (typeof window === "undefined") return null;
    const path = window.location.pathname;
    const m = path.match(/^\/bag\/([^/]+)\/?$/);
    if (m) return { type: "bag", code: decodeURIComponent(m[1]) };
    if (path === "/termos") return { type: "terms" };
    if (path === "/privacidade") return { type: "privacy" };
    return null;
  });

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, bagsRes, shipmentsRes, pointsRes, driversRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/bags`),
        axios.get(`${API}/shipments`),
        axios.get(`${API}/points`),
        axios.get(`${API}/drivers`),
      ]);
      setStats(statsRes.data);
      setBags(bagsRes.data);
      setShipments(shipmentsRes.data);
      setPoints(pointsRes.data);
      setDrivers(driversRes.data);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Role-based view gating: when the user logs in, send them straight to
  // their role's home view (defined in /lib/roleNav.js). Also block access
  // to forbidden views by snapping back to the role home.
  useEffect(() => {
    if (!user) return;
    import("@/lib/roleNav").then(({ getAllowedViewIds, getRoleConfig }) => {
      const cfg = getRoleConfig(user.role);
      const home = cfg?.home || "landing";
      const allowed = getAllowedViewIds(user.role);
      if (allowed && !allowed.has(currentView)) {
        setCurrentView(home);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role, currentView]);

  // After login (user.id changes), always send the user to their role's home
  useEffect(() => {
    if (!user) return;
    import("@/lib/roleNav").then(({ getRoleConfig }) => {
      const cfg = getRoleConfig(user.role);
      const home = cfg?.home || "landing";
      // Only redirect if currently sitting on the public landing
      if (currentView === "landing" && home !== "landing") {
        setCurrentView(home);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const createShipment = async (data) => {
    try {
      const res = await axios.post(`${API}/shipments`, data);
      if (res.data && !res.data.error) {
        await fetchData();
        return res.data;
      }
      return null;
    } catch (e) {
      console.error("Error creating shipment:", e);
      return null;
    }
  };

  // Public deep-link pages bypass auth entirely
  if (publicRoute?.type === "bag") {
    return <BagReputation bagCode={publicRoute.code} />;
  }
  if (publicRoute?.type === "terms") {
    return <LegalPage mode="terms" onBack={() => { window.location.href = "/"; }} />;
  }
  if (publicRoute?.type === "privacy") {
    return <LegalPage mode="privacy" onBack={() => { window.location.href = "/"; }} />;
  }

  // Show auth page if not logged in and not in demo mode
  if (authLoading) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="animate-fade-in" style={{ textAlign: "center" }}>
          <img src="/assets/logo.png" alt="Bluemove" style={{ height: 48, margin: "0 auto 12px" }} />
          <span style={{ fontFamily: "Outfit", fontWeight: 600, color: "var(--text-muted)", display: "block" }}>A carregar...</span>
        </div>
      </div>
    );
  }

  if (!user && user !== undefined && !demoMode) {
    if (!portalRole) {
      return (
        <RolePortal
          onPickRole={setPortalRole}
          onSkipDemo={() => setDemoMode(true)}
        />
      );
    }
    return (
      <AuthPage
        role={portalRole}
        onBack={() => setPortalRole(null)}
        onSkipDemo={() => setDemoMode(true)}
      />
    );
  }

  // Determine allowed views based on role
  const userRole = user?.role || "user";

  return (
    <div data-testid="app-container" style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {currentView !== "landing" && (
        <Navigation
          currentView={currentView}
          setCurrentView={setCurrentView}
          user={user}
          demoMode={demoMode}
          onLogout={logout}
        />
      )}
      {currentView === "landing" && (
        <LandingPage
          stats={stats}
          setCurrentView={setCurrentView}
          loading={loading}
          user={user}
          demoMode={demoMode}
          onLogout={logout}
        />
      )}
      {currentView === "user" && (
        <UserDemo shipments={shipments} points={points} bags={bags} createShipment={createShipment} loading={loading} />
      )}
      {currentView === "intermediary" && (
        <IntermediaryDashboard points={points} bags={bags} shipments={shipments} stats={stats} loading={loading} />
      )}
      {currentView === "driver" && (
        <DriverDashboard drivers={drivers} bags={bags} shipments={shipments} stats={stats} loading={loading} />
      )}
      {currentView === "map" && (
        <NetworkMap points={points} bags={bags} shipments={shipments} />
      )}
      {currentView === "analytics" && (
        <AnalyticsDashboard />
      )}
      {currentView === "beacons" && (
        <BeaconDashboard />
      )}
      {currentView === "tracking" && (
        <PublicTracking />
      )}
      {currentView === "my-shipments" && (
        <MyShipments />
      )}
      {currentView === "admin" && (userRole === "admin" || userRole === "super_admin") && (
        <AdminDashboard userRole={userRole} />
      )}
      {currentView === "fleet" && (
        <VehicleManager />
      )}
      {currentView === "routes" && (
        <RouteSearch />
      )}
      {currentView === "financial" && (
        <FinancialDashboard />
      )}
      {currentView === "gamification" && (
        <Gamification />
      )}
      {currentView === "integrations" && (
        <SuperAdminIntegrations />
      )}
      {currentView === "terms" && (
        <LegalPage mode="terms" onBack={() => setCurrentView(user ? "landing" : "landing")} />
      )}
      {currentView === "privacy" && (
        <LegalPage mode="privacy" onBack={() => setCurrentView("landing")} />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
